import React, { useEffect, useState } from "react";
import SiteLogo from "./SiteLogo";
import StatusMessage from "./StatusMessage";
import TopSponsor from "./TopSponsor";
import HeaderMenu from "./HeaderMenu";
import { SignInModal } from "./Modal";
import { useHistory } from "react-router-dom";
import {
  useAppUserDetailsSelector,
  useAppUserAuthSelector,
  useAppUserTokenSelector,
} from "src/_common/hooks/selectors/userSelector";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { MODAL_NAME, URLS } from "src/_config";
import { useModal } from "src/_common/hooks/custom/useModal";

const TopNav = ({ _page }: any) => {
  const { showModal, toggleShowModal } = useModal();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const history = useHistory();

  // User state selectors
  const userDetails = useAppUserDetailsSelector();
  const isAuthenticated = useAppUserAuthSelector();
  const token = useAppUserTokenSelector();
  const userAction = useAppUserAction();

  // Check if user is logged in (has token and is authenticated)
  const isLoggedIn = Boolean(token && isAuthenticated);
  const userName = userDetails?.display_name || userDetails?.first_name || userDetails?.email || "User";
  const userEmail = userDetails?.email || "";

  // Format user name for display (limit length)
  const displayName = userName.length > 15 ? userName.substring(0, 15) + "..." : userName;

  const handleSignInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleShowModal(MODAL_NAME.SIGN_IN, true);
  };

  const handleCloseSignInModal = () => {
    toggleShowModal(MODAL_NAME.SIGN_IN, false);
  };

  const handleAuthSuccess = () => {
    // Close the sign in modal when login is successful
    toggleShowModal(MODAL_NAME.SIGN_IN, false);
  };

  const handleUserDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowUserDropdown(!showUserDropdown);
  };

  const handleMyProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(URLS.AUTH.MY_PROFILE);
    setShowUserDropdown(false);
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    userAction.logout();
    setShowUserDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".user-dropdown-container")) {
        setShowUserDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showUserDropdown]);

  return (
    <React.Fragment>
      <div className="sticky-header-panel">
        <header>
          {/* Top Sponsor Panel */}
          <TopSponsor />

          {/* Logo Panel with Navigation */}
          <div className="logo-panel py-2">
            <div className="container">
              <div className="logo_Inner_Panel d-flex justify-content-between align-items-center">
                <div className="logo">
                  <SiteLogo />
                </div>
                <HeaderMenu />

                {/* Authentication Section */}
                {isLoggedIn ? (
                  // User is logged in - show welcome message and dropdown
                  <div className="user-dropdown-container position-relative">
                    <a
                      href="#"
                      className="auth_Link d-flex gap-2 align-items-center text-white font_Secondary"
                      onClick={handleUserDropdownToggle}
                      aria-expanded={showUserDropdown}
                      aria-haspopup="true"
                      role="button"
                      tabIndex={0}
                    >
                      <i className="profile_Icon"></i>
                      <span className="user-welcome-text">Welcome {displayName}</span>
                      <i className={`fas fa-chevron-${showUserDropdown ? "up" : "down"} ms-1`}></i>
                    </a>

                    {/* User Dropdown Menu */}
                    {showUserDropdown && (
                      <div className="user-dropdown-menu" role="menu">
                        <div className="dropdown-item user-info-header" role="menuitem">
                          <span className="user-name">{userName}</span>
                          <small className="user-email text-muted d-block">{userEmail}</small>
                        </div>
                        <div className="dropdown-divider"></div>
                        <a
                          href="javascript:void(0)"
                          className="dropdown-item"
                          onClick={handleMyProfileClick}
                          role="menuitem"
                        >
                          <i className="fas fa-user me-2"></i>
                          My Profile
                        </a>
                        <div className="dropdown-divider"></div>
                        <a href="javascript:void(0)" className="dropdown-item" onClick={handleLogout} role="menuitem">
                          <i className="fas fa-sign-out-alt me-2"></i>
                          Logout
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  // User is not logged in - show sign in link
                  <a
                    href="javascript:void(0)"
                    className="auth_Link d-flex gap-2 align-items-center text-white font_Secondary"
                    onClick={handleSignInClick}
                    role="button"
                    tabIndex={0}
                  >
                    <i className="profile_Icon"></i>Sign In
                  </a>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Status Message Component */}
        <StatusMessage />
      </div>
      {/* Sign In Modal */}
      <SignInModal
        isOpen={showModal[MODAL_NAME.SIGN_IN]}
        onClose={handleCloseSignInModal}
        onAuthSuccess={handleAuthSuccess}
      />
    </React.Fragment>
  );
};

export default TopNav;
